import type { CustomerDirectoryPublishGateCode } from 'pi-kiosk-shared';
import { apiClient, ApiError, type ApiResponse } from '../../../services/api/apiClient.js';
import {
  complianceApi,
  type AuditEventListResult,
} from '../../../services/api/compliance.api.js';
import { salesPointsApi } from '../../../services/api/salesPoints.api.js';

export { ApiError };

export async function fetchAccountSettingsAuditEvents(params: {
  tenantId: number;
  pageSize: number;
  eventCode: string;
}): Promise<ApiResponse<AuditEventListResult>> {
  return complianceApi.getAuditEvents({
    tenantId: params.tenantId,
    pageSize: params.pageSize,
    eventCode: params.eventCode,
  });
}

export async function fetchAdminPaymentPreferences(): Promise<
  ApiResponse<{
    preferences: {
      stripeIntegrationMode: 'DIRECT' | 'CONNECT';
      stripeConnectProductModel: 'SAAS_PLATFORM' | 'MARKETPLACE';
      customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
      /** Tenant Feature-policy purpose axis; omit until backend deploys (clients default BOTH). */
      allowedPurposes?: 'PRODUCT_ONLY' | 'DONATION_ONLY' | 'BOTH';
      /** Tenant currency allow-list (cascade root). */
      allowedCurrencies?: string[];
      /** Tenant default currency ∈ allow-list. */
      defaultCurrency?: string;
      /** Tenant daily cash limit (minor units); null = unlimited. */
      dailyCashLimitMinorPerTenant?: number | null;
      pickup?: {
        allowPreparingWhileUnpaid: boolean;
        pickupReadySmsEnabled: boolean;
        pickupSecondaryStaffEmail?: string | null;
        exposePickupCodeOnlyWhenPaid: boolean;
        scheduledPickupEnabled: boolean;
        scheduledPickupMinAdvanceMinutes: number;
        scheduledPickupMaxAdvanceDays: number;
        pickupWindowDurationMs: number;
        ordinaryRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
        preparedRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
        scheduledPickupSlotGranularityMinutes: number;
        autoMarkReadyAtSlotStart: boolean;
        readyLeadMinutes: number;
        maxConcurrentScheduledPickups: number;
        defaultDisplayTimeZone: string;
        lowStockHoldEnabled?: boolean;
        lowStockHoldThreshold?: number;
        lowStockHoldTtlMs?: number;
        /** Spec A15 / Q10 — restock alert email channel. */
        restockEmailChannelEnabled?: boolean;
        opsMode?: 'NORMAL' | 'DELAY' | 'PAUSE';
        opsBufferMs?: number | null;
        opsDurationMs?: number | null;
        opsEndsAt?: string | null;
        opsEndOfDayRestore?: boolean;
        /** Self-Scan selective check (FR-16) — default OFF. */
        tenantSelfScanSelectiveCheckEnabled?: boolean;
        /** Sample rate % when selective check is ON (1–100). */
        samplePercent?: number;
      };
    };
  }>
> {
  return apiClient.getAdminPaymentPreferences();
}

export async function fetchAdminProductPaymentCatalogReadiness(): Promise<
  ApiResponse<{
    ready: boolean;
    flags: {
      hasProduct: boolean;
      hasSalesPoint: boolean;
      hasStockedProduct: boolean;
      hasEnabledBankMethod: boolean;
      hasReadyPaymentMethod: boolean;
    };
    failedGates: readonly (
      | 'PRODUCT'
      | 'SALES_POINT'
      | 'STOCK'
      | 'BANK_METHOD'
      | 'METHOD_READINESS'
    )[];
    directoryEligibility: {
      listable: boolean;
      listedAt: string | null;
      hasPublishablePay: boolean;
      failedPublishGates: readonly CustomerDirectoryPublishGateCode[];
      stickyOos: boolean;
      delistRisk: boolean;
      /** G5 — cash Approach A remediation jump (not banks Ověřit). */
      cashApproachARemediation: boolean;
      salesPoints: readonly {
        salesPointId: number;
        listable: boolean;
        listedAt: string | null;
        hasPublishablePay: boolean;
        failedPublishGates: readonly string[];
        stickyOos: boolean;
        delistRisk: boolean;
      }[];
    };
  }>
> {
  return apiClient.getAdminProductPaymentCatalogReadiness();
}

/** Sales points with store codes for go-live customer shop preview (AC-15). */
export async function fetchTenantSalesPointsForGoLivePreview(): Promise<
  readonly {
    readonly id: number;
    readonly code?: string | null;
    readonly name: string;
    readonly isActive?: boolean;
  }[]
> {
  const response = await salesPointsApi.getAll();
  return response.data?.salesPoints ?? [];
}

export async function fetchAdminConfigurationProgressDismiss(): Promise<
  ApiResponse<{ dismissed: boolean }>
> {
  return apiClient.getAdminConfigurationProgressDismiss();
}

export async function updateAdminConfigurationProgressDismiss(body: {
  dismissed: boolean;
}): Promise<ApiResponse<{ dismissed: boolean }>> {
  return apiClient.setAdminConfigurationProgressDismiss(body);
}

export async function updateAdminPaymentPreferences(body: {
  customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
  /** Tenant currency allow-list (cascade root). */
  allowedCurrencies?: string[];
  /** Tenant default currency ∈ allow-list. */
  defaultCurrency?: string;
  /** Tenant daily cash limit (minor units); null = unlimited. */
  dailyCashLimitMinorPerTenant?: number | null;
  pickup?: {
    allowPreparingWhileUnpaid?: boolean;
    pickupReadySmsEnabled?: boolean;
    pickupSecondaryStaffEmail?: string | null;
    exposePickupCodeOnlyWhenPaid?: boolean;
    scheduledPickupEnabled?: boolean;
    scheduledPickupMinAdvanceMinutes?: number;
    scheduledPickupMaxAdvanceDays?: number;
    pickupWindowDurationMs?: number;
    ordinaryRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
    preparedRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
    scheduledPickupSlotGranularityMinutes?: number;
    autoMarkReadyAtSlotStart?: boolean;
    readyLeadMinutes?: number;
    maxConcurrentScheduledPickups?: number;
    defaultDisplayTimeZone?: string;
    lowStockHoldEnabled?: boolean;
    lowStockHoldThreshold?: number;
    lowStockHoldTtlMs?: number;
    /** Spec A15 / Q10 — restock alert email channel. */
    restockEmailChannelEnabled?: boolean;
    opsMode?: 'NORMAL' | 'DELAY' | 'PAUSE';
    opsBufferMs?: number | null;
    opsDurationMs?: number | null;
    opsEndsAt?: string | null;
    opsEndOfDayRestore?: boolean;
    restoreOpsToNormal?: boolean;
    /** Self-Scan selective check (FR-16) — default OFF. */
    tenantSelfScanSelectiveCheckEnabled?: boolean;
    /** Sample rate % when selective check is ON (1–100). */
    samplePercent?: number;
  };
}): Promise<ApiResponse<{ updated: boolean }>> {
  return apiClient.updateAdminPaymentPreferences(body);
}

export async function fetchStripeConnectOnboardingStatus(
  bankAccountId: string,
  railId: string
): Promise<
  ApiResponse<{
    status: {
      stripeIntegrationMode: 'DIRECT' | 'CONNECT';
      stripeConnectProductModel: 'SAAS_PLATFORM' | 'MARKETPLACE';
      stripeConnectOnboardingStatus: string;
      stripeConnectAccountId: string | null;
      chargesEnabled: boolean | null;
      payoutsEnabled: boolean | null;
      detailsSubmitted: boolean | null;
    };
  }>
> {
  return apiClient.getBankAccountStripeConnectOnboardingStatus(bankAccountId, railId);
}

export async function fetchAdminProviderCredentialPolicies(): Promise<
  ApiResponse<{
    stripeIntegrationMode?: 'DIRECT' | 'CONNECT';
    policies: Array<{
      providerKind: string;
      providerId: string;
      requiredSecretKeys: string[];
      optionalSecretKeys?: string[];
      bankAccountMode: 'forbidden' | 'optional' | 'required';
      ui?: {
        label?: string;
        helpText?: string;
        secretFieldLabels?: Record<string, string>;
      };
    }>;
  }>
> {
  return apiClient.getAdminProviderCredentialPolicies();
}

export async function startStripeConnectOnboarding(
  bankAccountId: string,
  railId: string
): Promise<ApiResponse<{ url: string; stripeConnectAccountId: string; expiresAt: number }>> {
  return apiClient.startBankAccountStripeConnectOnboarding(bankAccountId, railId);
}

export async function updateAdminUsername(body: { username: string }): Promise<
  ApiResponse<{ user: { id: number; email: string; username: string | null; name: string | null; role: string } }>
> {
  return apiClient.updateAdminUsername(body);
}

export async function updateAdminPreferredLocale(body: {
  preferredLocale: 'cs' | 'en' | 'sk';
}): Promise<ApiResponse<{ preferredLocale: 'cs' | 'en' | 'sk' }>> {
  return apiClient.updateAdminPreferredLocale(body);
}

export async function updateAdminPassword(body: {
  /** Required when rotating an existing password; omit for first set after OIDC link. */
  currentPassword?: string;
  newPassword: string;
}): Promise<ApiResponse<{ updated: boolean }>> {
  return apiClient.updateAdminPassword(body);
}

export async function fetchAdminBankAccountProviderPolicy(): Promise<
  ApiResponse<{
    stripeIntegrationMode: 'DIRECT' | 'CONNECT';
    slots: Array<{
      providerKind: string;
      providerSlotId: string;
      isEnabled: true;
    }>;
  }>
> {
  return apiClient.getAdminBankAccountProviderPolicy();
}

export async function upsertBankAccountRailCredentials(
  bankAccountId: string,
  railId: string,
  secrets: Record<string, string>
): Promise<ApiResponse<{ updated: boolean }>> {
  return apiClient.upsertBankAccountRailCredentials(bankAccountId, railId, secrets);
}

export async function deleteBankAccountRailCredential(
  bankAccountId: string,
  railId: string,
  secretKey: string
): Promise<ApiResponse<{ deleted: boolean }>> {
  return apiClient.deleteBankAccountRailCredential(bankAccountId, railId, secretKey);
}

export async function upsertBankAccountAttachmentCredentials(
  bankAccountId: string,
  attachmentId: string,
  secrets: Record<string, string>
): Promise<ApiResponse<{ updated: boolean }>> {
  return apiClient.upsertBankAccountAttachmentCredentials(bankAccountId, attachmentId, secrets);
}

export async function deleteBankAccountAttachmentCredential(
  bankAccountId: string,
  attachmentId: string,
  secretKey: string
): Promise<ApiResponse<{ deleted: boolean }>> {
  return apiClient.deleteBankAccountAttachmentCredential(bankAccountId, attachmentId, secretKey);
}

/** Tenant org approval lifecycle (Wave 1). */
export type TenantOrgApprovalStatus =
  | 'NOT_SUBMITTED'
  | 'PENDING_REVIEW'
  | 'APPROVED'
  | 'REJECTED';

export interface CompanyRegistryAddressDto {
  readonly street?: string;
  readonly city?: string;
  readonly zip?: string;
  readonly countryCode?: string;
}

export interface CompanyRegistryCandidateDto {
  readonly ico: string;
  readonly legalName: string;
  readonly dic?: string;
  readonly registeredAddress?: CompanyRegistryAddressDto;
  readonly registrySource?: 'ARES';
  readonly registryRecordId?: string;
}

export interface CompanyRegistryLookupResultDto {
  readonly status: 'ok' | 'empty' | 'unavailable';
  readonly candidates: readonly CompanyRegistryCandidateDto[];
  readonly allowManual: true;
}

/** Tenant publish gate — Track 2 dual-flow (may be absent on org-identity until BE exposes it). */
export type TenantPublishRestriction = 'NONE' | 'STRIKES' | 'PUBLISH_BANNED';

/** Soft-warn elevated KYB categories (alcohol / supplements). */
export type TenantOrgReviewRisk = 'STANDARD' | 'ELEVATED';

export interface TenantOrgIdentityDto {
  readonly orgApprovalStatus: TenantOrgApprovalStatus;
  readonly legalName?: string | null;
  readonly ico?: string | null;
  readonly dic?: string | null;
  readonly registeredAddress?: CompanyRegistryAddressDto | null;
  readonly orgIdentitySource?: string | null;
  readonly orgApprovalSubmittedAt?: string | null;
  readonly orgApprovalReviewedAt?: string | null;
  readonly orgApprovalReviewedByAdminUserId?: number | null;
  readonly orgApprovalRejectReason?: string | null;
  readonly livePublishedAt?: string | null;
  readonly livePublishedByAdminUserId?: number | null;
  /** Closed BusinessCategory enum code from BE. */
  readonly businessCategory?: string | null;
  /** Mandatory 50–500 on submit; may be null when not submitted. */
  readonly businessDescription?: string | null;
  readonly businessWebsiteUrl?: string | null;
  readonly businessSocialUrl?: string | null;
  /** Public seller contact — GoPay / MoR imprint HARD gate. */
  readonly contactEmail?: string | null;
  readonly contactPhone?: string | null;
  readonly reviewRisk?: TenantOrgReviewRisk | string | null;
  /**
   * Present when tenant trust API / org-identity includes publish gate.
   * AccountPublishBannedBanner treats missing as “not banned”.
   */
  readonly publishRestriction?: TenantPublishRestriction | string | null;
}

export interface SubmitTenantOrgIdentityBody {
  readonly ico: string;
  readonly legalName: string;
  readonly dic?: string;
  readonly registeredAddress?: CompanyRegistryAddressDto;
  /** Prefill marker from ARES candidate selection (mapped to BE `orgIdentitySource`). */
  readonly registrySource?: 'ARES';
  readonly registryRecordId?: string;
  /** Closed enum code — must match BE `BusinessCategory`. */
  readonly businessCategory: string;
  /** Mandatory 50–500 chars (trimmed). */
  readonly businessDescription: string;
  readonly businessWebsiteUrl?: string;
  readonly businessSocialUrl?: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
}

/**
 * ARES / company registry lookup — `ico` XOR `q`.
 * Matches BE `GET /api/v1/admin/company-registry/lookup`.
 */
export async function lookupCompanyRegistry(params: {
  readonly ico?: string;
  readonly q?: string;
}): Promise<ApiResponse<CompanyRegistryLookupResultDto>> {
  const search = new URLSearchParams();
  if (params.ico != null && params.ico.trim().length > 0) {
    search.set('ico', params.ico.trim());
  } else if (params.q != null && params.q.trim().length > 0) {
    search.set('q', params.q.trim());
  }
  return apiClient.get(`/api/v1/admin/company-registry/lookup?${search.toString()}`);
}

/** Current tenant org identity + approval status. */
export async function fetchTenantOrgIdentity(): Promise<ApiResponse<TenantOrgIdentityDto>> {
  return apiClient.get('/api/v1/admin/tenant/org-identity');
}

export interface TenantGoLiveDto {
  readonly orgApprovalStatus: TenantOrgApprovalStatus;
  readonly livePublishedAt: string | null;
  readonly livePublishedByAdminUserId: number | null;
  /** True when livePublishedAt was already set (idempotent 200). */
  readonly alreadyLive: boolean;
}

/**
 * Conscious go-live — stamps `livePublishedAt` (clears TENANT_LIVE).
 * Body must include `confirmGoLive: true` (ConfirmDialog). Idempotent 200 when already live.
 */
export async function publishTenantLive(): Promise<ApiResponse<TenantGoLiveDto>> {
  return apiClient.post('/api/v1/admin/tenant/go-live', { confirmGoLive: true });
}

/** Submit org identity → PENDING_REVIEW (`config:tenant:manage`). */
export async function submitTenantOrgIdentity(
  body: SubmitTenantOrgIdentityBody,
): Promise<ApiResponse<TenantOrgIdentityDto>> {
  return apiClient.put('/api/v1/admin/tenant/org-identity', {
    legalName: body.legalName,
    ico: body.ico,
    businessCategory: body.businessCategory,
    businessDescription: body.businessDescription,
    ...(body.dic !== undefined ? { dic: body.dic } : {}),
    ...(body.registeredAddress !== undefined
      ? { registeredAddress: body.registeredAddress }
      : {}),
    ...(body.registrySource != null ? { orgIdentitySource: body.registrySource } : {}),
    ...(body.businessWebsiteUrl !== undefined
      ? { businessWebsiteUrl: body.businessWebsiteUrl }
      : {}),
    ...(body.businessSocialUrl !== undefined
      ? { businessSocialUrl: body.businessSocialUrl }
      : {}),
    contactEmail: body.contactEmail,
    contactPhone: body.contactPhone,
  });
}

export type OnboardingNeedsPurpose = 'products' | 'donations' | 'both';
export type OnboardingNeedsSurfaces = 'kiosk' | 'mobile' | 'both';
export type OnboardingNeedsPickup = 'yes' | 'no';
export type OnboardingNeedsOperatingMode = 'mode1' | 'mode2' | 'mode3' | 'mode4';

export interface TenantOnboardingNeedsDto {
  readonly needsAnswersJson: Record<string, unknown> | null;
  readonly allowedPurposes: 'PRODUCT_ONLY' | 'DONATION_ONLY' | 'BOTH';
  readonly surfaceScope: 'KIOSK_ONLY' | 'CUSTOMER_ONLY' | 'BOTH';
}

export interface ApplyTenantOnboardingNeedsBody {
  readonly purpose: OnboardingNeedsPurpose;
  readonly surfaces: OnboardingNeedsSurfaces;
  readonly channels: {
    readonly kioskDeviceEnabled: boolean;
    readonly mobileShopEnabled: boolean;
  };
  readonly pickupHandoff?: OnboardingNeedsPickup | null;
  /** Optional operating-practice checkboxes (plan §8) — mirrored into needsAnswersJson when BE accepts. */
  readonly operatingModes?: readonly OnboardingNeedsOperatingMode[];
}

export interface ApplyTenantOnboardingNeedsResultDto {
  readonly allowedPurposes: 'PRODUCT_ONLY' | 'DONATION_ONLY' | 'BOTH';
  readonly surfaceScope: 'KIOSK_ONLY' | 'CUSTOMER_ONLY' | 'BOTH';
  readonly needsAnswersJson: Record<string, unknown>;
  readonly revision: number;
  readonly firstSalesPointId: number | null;
  readonly pickupHandoffApplied: OnboardingNeedsPickup | 'skipped_donation_only' | 'skipped';
}

/** GET needs answers — API compat / legacy mirror (`config:tenant:read`); wizard UI retired. */
export async function fetchTenantOnboardingNeeds(): Promise<ApiResponse<TenantOnboardingNeedsDto>> {
  return apiClient.get('/api/v1/admin/tenant/onboarding/needs');
}

/** Apply N1–N4 needs → entitlements/commerce (`config:tenant:manage`). */
export async function applyTenantOnboardingNeeds(
  body: ApplyTenantOnboardingNeedsBody,
): Promise<ApiResponse<ApplyTenantOnboardingNeedsResultDto>> {
  return apiClient.put('/api/v1/admin/tenant/onboarding/needs', body);
}

export type TenantOnboardingProgressItemId =
  | 'identity'
  | 'needs'
  | 'salesPoints'
  | 'bank'
  | 'test'
  | 'goLive';

export type TenantOnboardingProgressItemKind = 'needs-setup' | 'recommended';

export interface TenantOnboardingProgressItemDto {
  readonly id: TenantOnboardingProgressItemId;
  readonly done: boolean;
  readonly kind: TenantOnboardingProgressItemKind;
}

export interface TenantOnboardingProgressDto {
  readonly items: readonly TenantOnboardingProgressItemDto[];
  readonly percent: number;
  readonly onboardingLastStep: string | null;
  readonly onboardingBookmarkJson: Record<string, unknown> | null;
}

export interface UpdateTenantOnboardingBookmarkBody {
  readonly onboardingLastStep?: string | null;
  readonly onboardingBookmarkJson?: Record<string, unknown> | null;
}

export interface TenantOnboardingBookmarkDto {
  readonly onboardingLastStep: string | null;
  readonly onboardingBookmarkJson: Record<string, unknown> | null;
}

/** GET derived onboarding checklist (`config:tenant:read`) — ≠ configuration-progress-dismiss. */
export async function fetchTenantOnboardingProgress(): Promise<
  ApiResponse<TenantOnboardingProgressDto>
> {
  return apiClient.get('/api/v1/admin/tenant/onboarding-progress');
}

/** PUT onboarding bookmark (`config:tenant:manage`) — ban `{ dismissed }`. */
export async function updateTenantOnboardingBookmark(
  body: UpdateTenantOnboardingBookmarkBody,
): Promise<ApiResponse<TenantOnboardingBookmarkDto>> {
  return apiClient.put('/api/v1/admin/tenant/onboarding-bookmark', body);
}

